/* Iceberg */
import {
    Box,
    Button,
    Wizard,
    WizardContext,
    WizardPage
} from '@auviknetworks/iceberg-ui';
import { CircularProgress } from '@mui/material';

/*  React  */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

const LinkWrapper = ({ linkTo, buttonText, ...props }) => {
    if (!linkTo) {
        return (
            <Button
                data-trackid="partner_integrations_wizard_noLink"
                {...props}
            >
                {buttonText}
            </Button>
        );
    }
    return (
        <Link
            data-trackid="partner_integrations_wizard_link_routeTo"
            to={linkTo}
            {...props}
        >
            <Button data-trackid="partner_integrations_wizard_link">
                {buttonText}
            </Button>
        </Link>
    );
};

const CancelButton = ({ cancelLink }, { buttonText }) => (
    <LinkWrapper linkTo={cancelLink} buttonText={buttonText} />
);

const NextButton = ({ nextFn, previousFn, disableNext, hidePrevious }) => {
    const { toNextStep, toPreviousStep } = useContext(WizardContext);
    const { t } = useTranslation();
    return (
        <>
            {!hidePrevious && (
                <Button
                    data-trackid="partner_integrations_wizard_next_previous"
                    onClick={async () => {
                        let goBack = true;
                        if (previousFn) goBack = await previousFn();
                        if (goBack) toPreviousStep();
                    }}
                >
                    {t('Common.previous')}
                </Button>
            )}
            <Button
                data-trackid="partner_integrations_wizard_next_next"
                onClick={async () => {
                    let goNext = true;
                    if (nextFn) goNext = await nextFn();
                    if (goNext) toNextStep();
                }}
                disabled={disableNext}
            >
                {t('Common.next')}
            </Button>
        </>
    );
};

const DoneButton = ({ doneFn, previousFn, hidePrevious, doneLink }) => {
    const { toPreviousStep } = useContext(WizardContext);
    const { t } = useTranslation();
    return (
        <>
            {!hidePrevious && (
                <Button
                    data-trackid="partner_integrations_wizard_done_previous"
                    onClick={async () => {
                        if (previousFn) await previousFn();
                        toPreviousStep();
                    }}
                >
                    {t('Common.previous')}
                </Button>
            )}
            <LinkWrapper
                linkTo={doneLink}
                buttonText={t('Common.done')}
                onClick={async () => {
                    if (doneFn) await doneFn();
                }}
            />
        </>
    );
};

export const PartnerIntegrationWizard = ({
    steps,
    sharedData,
    cancelLink,
    loading
}) => {
    const { t } = useTranslation();

    if (loading) {
        return (
            <Box
                data-trackid="partnerIntegrationWizardRenderLoading"
                display="flex"
                justifyContent="center"
                width="100%"
                className="no-print"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Wizard
            align="Vertical"
            allowStepsNav={false}
            cancelButton={
                <CancelButton
                    cancelLink={cancelLink}
                    buttonText={t('Common.cancel')}
                />
            }
            initialStep={0}
            showLabel
            stepLabel={t('Common.step')}
            wizardFooterProps={{ display: 'none' }}
        >
            {steps.map((step) => {
                const {
                    buttonType,
                    component: Component,
                    disableNext,
                    doneFn,
                    doneLink,
                    hidePrevious,
                    labelText,
                    nextFn,
                    number,
                    previousFn,
                    props
                } = step;
                let buttons;
                if (buttonType === 'First') {
                    buttons = (
                        <NextButton
                            nextFn={nextFn}
                            disableNext={disableNext}
                            hidePrevious
                        />
                    );
                } else if (buttonType === 'Next') {
                    buttons = (
                        <NextButton
                            nextFn={nextFn}
                            previousFn={previousFn}
                            disableNext={disableNext}
                        />
                    );
                } else {
                    buttons = (
                        <DoneButton
                            doneFn={doneFn}
                            previousFn={previousFn}
                            hidePrevious={hidePrevious}
                            doneLink={doneLink}
                        />
                    );
                }
                return (
                    <WizardPage
                        key={`step_${number}`}
                        labelText={labelText}
                        step={number}
                        buttons={() => buttons}
                    >
                        <Box p="24px" m="0" width="100%">
                            <Component {...sharedData} {...props} />
                        </Box>
                    </WizardPage>
                );
            })}
        </Wizard>
    );
};
